import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Stack, Typography } from '@mui/material'
import { LangFilter } from 'Components/Section/Molecules'
import { KoreanLanguagePathTitle } from 'Constants/Navigation'
import { ChevronDownIcon } from 'Icons/Chevron'
import { PlusCircleFilledIcon } from 'Icons/Plus'
import { ReactNode, useCallback, useEffect, useState } from 'react'

const accordionPaperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
  '&:before': { display: 'none' },
  boxShadow: 'none',
  overflow: 'hidden',
} as const

type Props = {
  contentAddTitle: string
  schoolSectionAddTitle: string
  onAddContent: () => void
  onAddSchools: () => void
  contentBody: ReactNode
  schoolsBody: ReactNode
  expandSchoolsSection?: boolean
}

export const KoreanLanguageExemplarySections = ({
  contentAddTitle,
  schoolSectionAddTitle,
  onAddContent,
  onAddSchools,
  contentBody,
  schoolsBody,
  expandSchoolsSection,
}: Props) => {
  const [textsOpen, setTextsOpen] = useState(true)
  const [schoolsOpen, setSchoolsOpen] = useState(false)

  useEffect(() => {
    if (expandSchoolsSection) setSchoolsOpen(true)
  }, [expandSchoolsSection])

  const onTextsChange = useCallback((_: React.SyntheticEvent, expanded: boolean) => setTextsOpen(expanded), [])
  const onSchoolsChange = useCallback((_: React.SyntheticEvent, expanded: boolean) => setSchoolsOpen(expanded), [])

  return (
    <Grid container rowSpacing={2.5}>
      <Grid item xs={12}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.action.hover),
          }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent='space-between' spacing={1.5}>
            <Typography variant='body2' color='text.secondary' maxWidth={{ sm: 360 }}>
              Язык заголовков в списках ниже применяется ко всему разделу образцовых школ.
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <LangFilter />
            </Box>
          </Stack>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Accordion expanded={textsOpen} onChange={onTextsChange} elevation={0} disableGutters sx={accordionPaperSx}>
          <AccordionSummary
            expandIcon={
              <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <ChevronDownIcon />
              </Box>
            }
            sx={{
              px: 2,
              py: 1.25,
              minHeight: 56,
              '& .MuiAccordionSummary-expandIconWrapper': { transition: 'transform 0.2s ease' },
              '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(180deg)' },
            }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2} width='100%' pr={1}>
              <Box minWidth={0}>
                <Typography variant='subtitle1' fontWeight={700} component='span' display='block'>
                  Тексты и материалы
                </Typography>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Публикации для страницы раздела
                </Typography>
              </Box>
              <Box onClick={e => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                <Button size='medium' variant='contained' startIcon={<PlusCircleFilledIcon />} onClick={onAddContent}>
                  {contentAddTitle}
                </Button>
              </Box>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>{contentBody}</AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Accordion expanded={schoolsOpen} onChange={onSchoolsChange} elevation={0} disableGutters sx={accordionPaperSx}>
          <AccordionSummary
            expandIcon={
              <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <ChevronDownIcon />
              </Box>
            }
            sx={{
              px: 2,
              py: 1.25,
              minHeight: 56,
              '& .MuiAccordionSummary-expandIconWrapper': { transition: 'transform 0.2s ease' },
              '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(180deg)' },
            }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2} width='100%' pr={1}>
              <Box minWidth={0}>
                <Typography variant='subtitle1' fontWeight={700} component='span' display='block'>
                  {KoreanLanguagePathTitle.exemplary}
                </Typography>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Регионы и учёт образцовых школ корейского языка
                </Typography>
              </Box>
              <Box onClick={e => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                <Button size='medium' variant='outlined' startIcon={<PlusCircleFilledIcon />} onClick={onAddSchools}>
                  {schoolSectionAddTitle}
                </Button>
              </Box>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>{schoolsBody}</AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  )
}
